import { createApi } from '@reduxjs/toolkit/query/react';

import { PERFORMANCE_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

/**
 * A mapped Needs By Date object.
 * @typedef {Object} NeedsByDate
 * @property {string} needsByDate - The needs by date.
 */

const mapValueCaseSummaries = (response) =>
    (response ?? []).map((valueCase) => ({
        valueCaseId: valueCase.value_case_id,
        valueCaseName: valueCase.value_case_name,
        segmentId: valueCase.segment_id,
        productServiceOids: (valueCase.product_service_incentives ?? []).map(
            (incentive) => incentive.product_service_oid,
        ),
    }));

export const performanceApi = createApi({
    baseQuery: getBaseQuery(PERFORMANCE_API_URL),
    tagTypes: ['NeedsByDate'],
    reducerPath: 'performanceApi',
    endpoints: (build) => ({
        getReferralSupportCapability: build.query({
            query: ({ referralId }) => `admin/referral-support-capability/${referralId}`,
            transformResponse: (response) => response?.has_capability,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving whether the pharmacy has capability to view the referral.',
            ),
        }),
        getValueCases: build.query({
            query: () => 'admin/value-case-summaries',
            transformResponse: mapValueCaseSummaries,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving the value cases.',
            ),
        }),
        getAdHocValueCases: build.query({
            query: () => 'admin/value-case-summaries/ad-hoc',
            transformResponse: mapValueCaseSummaries,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving the ad hoc value cases.',
            ),
        }),
        getAdHocValueCaseEligibility: build.query({
            query: ({ referralId }) =>
                `admin/referrals/${referralId}/ad-hoc-value-case-eligibility`,
        }),
        getNeedsByDate: build.query({
            query: ({ treatmentId }) => `admin/needs_by_date/${treatmentId}`,
            providesTags: (_results, _error, { treatmentId }) => [
                { type: 'NeedsByDate', id: treatmentId },
            ],
            /**
             * Maps a needs by date from the API to a needs by date object.
             * @param {Object} response - The response to map.
             * @returns {NeedsByDate} The mapped needs by date object.
             */
            transformResponse: (response) => ({
                needsByDate: response?.needs_by_date,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving needs by date for the treatment.',
            ),
        }),
        updateNeedsByDate: build.mutation({
            query: ({ treatmentId, body }) => ({
                url: `admin/needs_by_date/${treatmentId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_results, _error, { treatmentId }) => [
                { type: 'NeedsByDate', id: treatmentId },
            ],
        }),
    }),
});

export const {
    useGetReferralSupportCapabilityQuery,
    useGetValueCasesQuery,
    useGetAdHocValueCasesQuery,
    useGetAdHocValueCaseEligibilityQuery,
    useGetNeedsByDateQuery,
    useUpdateNeedsByDateMutation,
} = performanceApi;
